import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import UsersTable from '@/components/table/user/UsersTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { capitalizeAndJoin } from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionUser } from '@/interfaces/api/user/ICollectionUser';
import { IUserWithAction } from '@/interfaces/api/user/IUserWithAction';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const Users = ({
	onSelectUser,
}: {
	onSelectUser?: (username: string) => void;
}) => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchQueryUsername, setSearchQueryUsername] = useState('');
	const [searchQueryName, setSearchQueryName] = useState('');

	const filter = {
		'page[size]': pageSize,
		'page[number]': pageNumber,
		...(searchQueryUsername && { 'filter[username]': searchQueryUsername }),
		...(searchQueryName && { 'filter[name]': searchQueryName }),
	};

	const {
		data: users,
		isLoading: userIsLoading,
		isError: userIsError,
		error: userError,
		refetch: userRefetch,
	} = useQuery<ICollectionUser>({
		queryKey: [ApiQueryKeys.getAllMerchants, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllMerchants({
				params: filter,
			}),
		refetchOnWindowFocus: false,
	});

	if (userIsError) {
		if (userError instanceof Error) {
			notificationService.error(userError.message);
		} else {
			notificationService.error('Unknown error fetching merchants');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionUser,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const columnHelper = createColumnHelper<IUserWithAction>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => (
				<div className="min-w-[40px] w-[40px] sm:w-[50px] text-xs sm:text-sm truncate">
					{info.getValue()}
				</div>
			),
			header: () => (
				<div className="min-w-[40px] w-[40px] sm:w-[50px] text-xs sm:text-sm">
					ID
				</div>
			),
		}),
		columnHelper.accessor('name', {
			cell: (info) => (
				<div className="min-w-[80px] w-[80px] sm:w-[120px] lg:w-[150px] text-xs sm:text-sm truncate">
					<span title={info.getValue()}>{info.getValue()}</span>
				</div>
			),
			header: () => (
				<div className="min-w-[80px] w-[80px] sm:w-[120px] lg:w-[150px] text-xs sm:text-sm">
					Name
				</div>
			),
		}),
		columnHelper.accessor('username', {
			cell: (info) => (
				<div className="min-w-[120px] w-[120px] sm:w-[180px] lg:w-[250px] text-xs sm:text-sm truncate">
					<span title={info.getValue()}>{info.getValue()}</span>
				</div>
			),
			header: () => (
				<div className="min-w-[120px] w-[120px] sm:w-[180px] lg:w-[250px] text-xs sm:text-sm">
					Username
				</div>
			),
		}),
		columnHelper.accessor('store', {
			cell: (info) => (
				<div className="min-w-[100px] w-[100px] sm:w-[150px] lg:w-[200px] text-xs sm:text-sm truncate">
					<span title={info.getValue()?.tradeName}>
						{info.getValue()?.tradeName || 'N/A'}
					</span>
				</div>
			),
			header: () => (
				<div className="min-w-[100px] w-[100px] sm:w-[150px] lg:w-[200px] text-xs sm:text-sm">
					Store
				</div>
			),
		}),
		columnHelper.accessor('roles', {
			cell: (info) => (
				<div className="min-w-[80px] w-[80px] sm:w-[100px] lg:w-[130px] text-xs sm:text-sm truncate">
					<span title={capitalizeAndJoin(info.getValue())}>
						{capitalizeAndJoin(info.getValue())}
					</span>
				</div>
			),
			header: () => (
				<div className="min-w-[80px] w-[80px] sm:w-[100px] lg:w-[130px] text-xs sm:text-sm">
					Roles
				</div>
			),
		}),
	];

	const filteredUsers = users
		? {
				...users,
				data: onSelectUser
					? users.data.filter((user) => !user.roles.includes('consumer'))
					: users.data,
		  }
		: null;

	return (
		<div
			className="min-h-content overflow-x-auto overflow-y-auto"
			data-test="merchants-dashboard"
		>
			{userIsLoading && <LoadingSkeletonModalTable />}
			{filteredUsers && !userIsLoading && (
				<div className="flex flex-col h-full grow gap-4 min-w-fit">
					<UsersTable
						collectionUser={filteredUsers}
						paginationOption={handlePaginationOptions(filteredUsers)}
						columns={columns}
						refetch={userRefetch}
						usernameQuery={searchQueryUsername}
						setUsernameQuery={setSearchQueryUsername}
						nameQuery={searchQueryName}
						setNameQuery={setSearchQueryName}
						users={filteredUsers.itemCount}
						onSelectUser={onSelectUser}
					/>
				</div>
			)}
		</div>
	);
};

export default Users;
